import { create } from "zustand";
import { persist } from "zustand/middleware";

export type AuthState = {
  username: string;
  dk: string;
  tk: string;
  refreshToken?: string;
  ePuK: string;
  ePrK: string;
  encryptedPassword?: string;
  // Password KDF scheme for the logged-in account: 0 = v1 (dk == mk), 2 = v2
  // (dk == kek, credential == authSecret, username salt), 3 = v2 split + random
  // salt. Drives scheme-aware password checks.
  kdfVersion: number;
  // Per-user random Argon2id salt (hex) for v3; "" for v1/v2. Persisted so
  // in-session re-derivation (isPasswordValid, changePassword) works without a
  // server round-trip. Public — it's a salt, not a secret.
  kdfSalt: string;
};

type Actions = {
  setUsername: (val: string) => void;
  setDerivedKey: (val: string) => void;
  setToken: (val: string) => void;
  setRefreshToken: (val: string) => void;
  setPubKey: (val: string) => void;
  setPrivKey: (val: string) => void;
  setEncryptedPassword: (val: string | undefined) => void;
  setKdfVersion: (val: number) => void;
  setKdfSalt: (val: string) => void;
  resetAuthState: () => void;
};

const initialState: AuthState = {
  username: "",
  dk: "",
  tk: "",
  refreshToken: "",
  ePuK: "",
  ePrK: "",
  encryptedPassword: "",
  kdfVersion: 0,
  kdfSalt: "",
};

export const useAuthState = create<AuthState & Actions>()(
  persist(
    (set) => ({
      // State
      ...initialState,

      // Actions
      setUsername: (username) => {
        set({
          username,
        });
      },

      setDerivedKey: (dk) => {
        set({
          dk,
        });
      },

      setToken: (tk) => {
        set({
          tk,
        });
      },

      setRefreshToken: (refreshToken) => {
        set({
          refreshToken,
        });
      },

      setPubKey: (ePuK) => {
        set({
          ePuK,
        });
      },

      setPrivKey: (ePrK) => {
        set({
          ePrK,
        });
      },
      setEncryptedPassword: (encryptedPassword) => {
        set({
          encryptedPassword,
        });
      },
      setKdfVersion: (kdfVersion) => {
        set({
          kdfVersion,
        });
      },
      setKdfSalt: (kdfSalt) => {
        set({
          kdfSalt,
        });
      },

      // RESET
      resetAuthState: () => {
        set(initialState);
      },
    }),
    {
      name: "auth-state",
      version: 2,
      // Never persist `dk` (the master key / private-key KEK) to localStorage.
      // It is held in memory and sealed under a non-extractable WebCrypto key in
      // IndexedDB (see lib/crypto/sealedKey.ts), so a storage snapshot can't
      // yield a usable key. On reload useAuthSession unseals it back into memory.
      // Everything else stays persisted: `ePrK` is ciphertext (useless without
      // `dk`), `ePuK` is public, tokens are session creds, `encryptedPassword`
      // is the recovery blob (keyed by the recovery phrase, not `dk`).
      partialize: (state) => {
        const { dk: _dk, ...rest } = state;
        return rest;
      },
      migrate: (persisted, version) => {
        const state = { ...(persisted as Record<string, unknown>) };
        if (version < 2) {
          delete state.avatar;
        }
        // NOTE: do NOT strip a legacy plaintext `dk` here. A pre-sealing client
        // persisted `dk`; it must survive rehydration so useAuthSession can seal
        // it once (seamless migration, no forced re-login). useAuthSession then
        // purges the plaintext copy from storage after sealing.
        return state;
      },
    },
  ),
);
